class TrSiteLoader extends HTMLElement {
  selectors = {
    logo: '[data-tr-js-loader-logo]',
    rows: '[data-tr-js-loader-row]',
    blocks: '[data-tr-js-loader-block]'
  };

  attributes = {
    holdDuration: 'data-hold-duration',
    shutterDuration: 'data-shutter-duration',
    showOnMobile: 'data-show-on-mobile',
    repeatOnLoad: 'data-repeat-on-load'
  };

  storageKey = 'tr-site-loader-shown';

  connectedCallback() {
    this.logoElement = this.querySelector(this.selectors.logo);
    this.rowElements = Array.from(this.querySelectorAll(this.selectors.rows));
    this.animations = [];

    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    if (this.shouldSkipAnimation()) {
      this.hide(false);
      return;
    }

    document.documentElement.classList.add('tr-is-loading');
    document.dispatchEvent(new CustomEvent('tr:loader:start'));

    if (window.gsap) {
      this.playAnimation();
      return;
    }

    this.playFallbackAnimation();
  }

  shouldSkipAnimation() {
    return (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      (this.getAttribute(this.attributes.showOnMobile) !== 'true' && window.innerWidth < 990) ||
      (!this.shouldRepeatOnLoad() && this.wasShown())
    );
  }

  playAnimation() {
    const holdDuration = Number(this.getAttribute(this.attributes.holdDuration));
    const shutterDuration = Number(this.getAttribute(this.attributes.shutterDuration));
    this.timeline = window.gsap.timeline({
      onComplete: () => this.hide(true)
    });

    window.gsap.set(this.logoElement, {
      autoAlpha: 0,
      yPercent: -130,
      scale: 0.86,
      rotation: -3
    });

    this.timeline
      .to(this.logoElement, {
        autoAlpha: 1,
        yPercent: 0,
        scale: 1,
        rotation: 0,
        duration: 0.75,
        ease: 'expo.out'
      })
      .to(this.logoElement, {
        yPercent: -3,
        scale: 1.025,
        duration: 0.28,
        ease: 'sine.inOut'
      })
      .to(this.logoElement, {
        yPercent: 0,
        scale: 1,
        duration: 0.28,
        ease: 'sine.inOut'
      })
      .to(this.logoElement, {
        autoAlpha: 0,
        yPercent: 120,
        scale: 0.92,
        rotation: 3,
        duration: 0.65,
        ease: 'expo.in',
        delay: holdDuration
      });

    this.rowElements.forEach((rowElement, index) => {
      const blocks = rowElement.querySelectorAll(this.selectors.blocks);
      const from = index % 2 === 0 ? 'end' : 'start';

      this.timeline.to(
        blocks,
        {
          scaleX: 0,
          duration: shutterDuration,
          ease: 'power2.inOut',
          stagger: {
            from,
            amount: 0.45
          }
        },
        index === 0 ? '>-0.1' : '<'
      );
    });
  }

  async playFallbackAnimation() {
    const holdDuration = Number(this.getAttribute(this.attributes.holdDuration)) * 1000;
    const shutterDuration = Number(this.getAttribute(this.attributes.shutterDuration)) * 1000;
    const entryDuration = 750;
    const pulseDuration = 560;
    const exitDuration = 650;
    const animationDuration = entryDuration + pulseDuration + holdDuration + exitDuration;
    const logoAnimation = this.logoElement.animate(
      [
        { opacity: 0, transform: 'translateY(-130%) scale(0.86) rotate(-3deg)' },
        { opacity: 1, offset: entryDuration / animationDuration, transform: 'translateY(0) scale(1) rotate(0)' },
        { opacity: 1, offset: (entryDuration + pulseDuration / 2) / animationDuration, transform: 'translateY(-3%) scale(1.025) rotate(0)' },
        { opacity: 1, offset: (entryDuration + pulseDuration + holdDuration) / animationDuration, transform: 'translateY(0) scale(1) rotate(0)' },
        { opacity: 0, transform: 'translateY(120%) scale(0.92) rotate(3deg)' }
      ],
      {
        duration: animationDuration,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'forwards'
      }
    );

    this.animations.push(logoAnimation);
    await logoAnimation.finished;

    const shutterAnimations = this.rowElements.flatMap((rowElement, index) => {
      const blocks = Array.from(rowElement.querySelectorAll(this.selectors.blocks));
      const orderedBlocks = index % 2 === 0 ? blocks.reverse() : blocks;

      return orderedBlocks.map((block, blockIndex) => {
        const animation = block.animate(
          [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }],
          {
            duration: shutterDuration,
            delay: (blockIndex / Math.max(orderedBlocks.length - 1, 1)) * 500,
            easing: 'ease-in-out',
            fill: 'forwards'
          }
        );

        this.animations.push(animation);
        return animation.finished;
      });
    });

    await Promise.all(shutterAnimations);
    this.hide(true);
  }

  wasShown() {
    try {
      return sessionStorage.getItem(this.storageKey) === 'true';
    } catch {
      return false;
    }
  }

  setShown() {
    if (this.shouldRepeatOnLoad()) {
      return;
    }

    try {
      sessionStorage.setItem(this.storageKey, 'true');
    } catch {
      return;
    }
  }

  hide(shouldStore) {
    if (shouldStore) {
      this.setShown();
    }
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('tr-is-loading');
    document.dispatchEvent(new CustomEvent('tr:loader:end'));
  }

  destroy() {
    this.timeline?.kill();
    this.animations?.forEach((animation) => animation.cancel());
    window.gsap?.killTweensOf(this.logoElement);
    this.rowElements?.forEach((rowElement) => {
      window.gsap?.killTweensOf(rowElement.querySelectorAll(this.selectors.blocks));
    });
  }

  shouldRepeatOnLoad() {
    return this.getAttribute(this.attributes.repeatOnLoad) === 'true';
  }
}

customElements.define('tr-site-loader', TrSiteLoader);
